import { Link } from "react-router-dom";
import { Panel } from "@/components/Widgets";
import { CaptionButton } from "@/components/CaptionDialog";

const Tag = ({ children, tone = "g" }) => (
  <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border ${tone === "a" ? "border-[#C05C3D] text-[#C05C3D]" : tone === "o" ? "border-[#B08A1E] text-[#8A6C14]" : "border-[#2A4B3C] text-[#2A4B3C]"}`}>{children}</span>
);
const prioTone = (p) => (p === "alta" ? "a" : p === "media" ? "o" : "g");

const Frequency = ({ f }) => {
  const items = [["Reel IG / sett.", f.instagram_reel_settimana], ["Caroselli IG / sett.", f.instagram_caroselli_settimana], ["Post IG / sett.", f.instagram_post_settimana], ["Stories / giorno", f.stories_giorno], ["Post FB / sett.", f.facebook_post_settimana]];
  return (
    <Panel testid="frequency-panel" title="Frequenza di pubblicazione consigliata" className="lg:col-span-2" action={<Link to="/calendario" data-testid="open-calendar-link" className="text-xs font-semibold text-[#2A4B3C] border border-[#2A4B3C] px-3 py-1.5 rounded-full hover:bg-[#2A4B3C] hover:text-white transition-colors">Pianifica nel calendario</Link>}>
      <div className="grid grid-cols-2 md:grid-cols-5 border-t border-l border-[#E8E8E4]">
        {items.map(([l, v]) => <div key={l} className="border-r border-b border-[#E8E8E4] p-4"><div className="font-mono-num text-3xl text-[#2A4B3C]">{v ?? "-"}</div><div className="text-xs text-[#5C5C58] mt-1">{l}</div></div>)}
      </div>
      {f.note && <p className="text-sm text-[#5C5C58] mt-4">{f.note}</p>}
    </Panel>
  );
};

const Ideas = ({ list }) => (
  <Panel testid="ideas-panel" title="Idee per nuovi contenuti" sub={`${list.length} idee ordinate per priorità`}>
    <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-[#E8E8E4]">
      {list.map((c, i) => (
        <div key={i} data-testid={`idea-${i}`} className="border-r border-b border-[#E8E8E4] p-5 hover:bg-[#FAFAF8] transition-colors">
          <div className="flex flex-wrap gap-1.5 mb-3"><Tag>{c.formato}</Tag><Tag tone="o">{c.piattaforma}</Tag><Tag tone={prioTone(c.priorita)}>{c.priorita}</Tag><span className="text-[10px] uppercase tracking-wider text-[#5C5C58] self-center ml-1">{c.obiettivo}</span></div>
          <h4 className="font-display text-lg font-semibold leading-snug">{c.titolo}</h4>
          <p className="text-sm mt-2 border-l-2 border-[#C05C3D] pl-3 italic">"{c.hook}"</p>
          <p className="text-sm text-[#5C5C58] mt-2">{c.descrizione}</p>
          <p className="text-xs mt-3"><span className="label !text-[9px] mr-2">CTA</span>{c.cta}</p>
          <CaptionButton idea={c} index={i} />
        </div>
      ))}
    </div>
  </Panel>
);

const Funnel = ({ list }) => (
  <Panel testid="funnel-panel" title="Funnel dal follower al cliente">
    <div className="space-y-0">
      {list.map((f, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-[180px_1fr_200px] border-b border-[#E8E8E4] py-4 gap-3" style={{ paddingLeft: `${i * 10}px` }}>
          <div><div className="font-mono-num text-xs text-[#C05C3D]">0{i + 1}</div><div className="font-display font-semibold">{f.fase}</div></div>
          <div><p className="text-sm">{f.obiettivo}</p><ul className="text-sm text-[#5C5C58] mt-1 list-disc pl-4">{(f.tattiche || []).map((t, j) => <li key={j}>{t}</li>)}</ul></div>
          <div className="text-xs"><span className="label !text-[9px] block mb-1">KPI</span>{f.kpi}</div>
        </div>
      ))}
    </div>
  </Panel>
);

const Week = ({ list }) => (
  <Panel testid="week-plan" title="Piano editoriale settimanale">
    <div className="grid grid-cols-1 md:grid-cols-7 border-t border-l border-[#E8E8E4]">
      {list.map((d, i) => (
        <div key={i} className="border-r border-b border-[#E8E8E4] p-4">
          <div className="label !text-[9px]">{d.giorno}</div>
          <div className="text-sm mt-2 font-medium">{d.contenuto}</div>
          <div className="text-[11px] text-[#5C5C58] mt-2">{d.formato} · {d.piattaforma}</div>
        </div>
      ))}
    </div>
  </Panel>
);

const SimpleList = ({ title, items, testid, accent }) => (
  <Panel testid={testid} title={title}>
    <ul className="space-y-2">{(items || []).map((t, i) => <li key={i} className="text-sm flex gap-3"><span className={`font-mono-num text-xs pt-0.5 ${accent ? "text-[#C05C3D]" : "text-[#2A4B3C]"}`}>{String(i + 1).padStart(2, "0")}</span>{t}</li>)}</ul>
  </Panel>
);

export default function StrategyView({ s }) {
  const h = s.hashtag || {};
  return (
    <div className="space-y-4" data-testid="strategy-view">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="bg-[#2A4B3C] text-[#F7F7F5] p-6 rise" data-testid="health-score">
          <div className="label !text-[#C9D6CE]">Salute dei canali</div>
          <div className="font-mono-num text-6xl mt-4">{s.punteggio_salute}<span className="text-xl text-[#C9D6CE]">/100</span></div>
        </div>
        <Panel title="Sintesi" className="lg:col-span-3" testid="summary-panel"><p className="text-base leading-relaxed">{s.sintesi}</p></Panel>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(s.insight_chiave || []).map((k, i) => (
          <div key={i} className={`bg-white border border-[#E8E8E4] border-t-4 p-5 rise ${k.tipo === "negativo" ? "border-t-[#C05C3D]" : k.tipo === "opportunita" ? "border-t-[#C9A227]" : "border-t-[#2A4B3C]"}`} data-testid={`insight-${i}`}>
            <div className="label !text-[9px]">{k.tipo}</div><h4 className="font-display font-semibold mt-2">{k.titolo}</h4><p className="text-sm text-[#5C5C58] mt-1">{k.descrizione}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {s.frequenza && <Frequency f={s.frequenza} />}
        <Panel testid="best-times" title="Orari migliori">
          <ul className="divide-y divide-[#E8E8E4]">{(s.orari_migliori || []).map((o, i) => <li key={i} className="py-2 text-sm"><div className="flex justify-between"><span className="font-medium">{o.giorno}</span><span className="font-mono-num text-[#2A4B3C]">{o.orario}</span></div><div className="text-xs text-[#5C5C58]">{o.piattaforma} · {o.motivo}</div></li>)}</ul>
        </Panel>
      </div>
      <Ideas list={s.idee_contenuti || []} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel testid="keywords-panel" title="Keyword da usare" sub="In caption, testo a schermo, bio e SEO Instagram">
          <div className="divide-y divide-[#E8E8E4]">{(s.keyword || []).map((k, i) => (
            <div key={i} className="py-2.5 grid grid-cols-[1fr_auto] gap-2"><div><div className="text-sm font-semibold">{k.keyword}</div><div className="text-xs text-[#5C5C58]">{k.uso}</div></div><div className="flex gap-1 items-start"><Tag tone={k.volume === "alto" ? "a" : k.volume === "medio" ? "o" : "g"}>{k.volume}</Tag><Tag>{k.intento}</Tag></div></div>
          ))}</div>
        </Panel>
        <Panel testid="hashtags-panel" title="Set di hashtag">
          {[["Brand", h.brand], ["Nicchia", h.nicchia], ["Ampi", h.ampi], ["Da evitare", h.da_evitare]].map(([l, arr]) => (
            <div key={l} className="mb-4"><div className="label !text-[9px] mb-2">{l}</div><div className="flex flex-wrap gap-1.5">{(arr || []).map((t) => <span key={t} className={`text-xs border px-2 py-0.5 ${l === "Da evitare" ? "border-[#C05C3D] text-[#C05C3D] line-through" : "border-[#E8E8E4]"}`}>{t.startsWith("#") ? t : `#${t}`}</span>)}</div></div>
          ))}
        </Panel>
      </div>
      <Panel testid="trends-panel" title="Trend social da cavalcare">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 border-t border-l border-[#E8E8E4]">
          {(s.trend || []).map((t, i) => <div key={i} className="border-r border-b border-[#E8E8E4] p-4"><h4 className="font-display font-semibold text-sm">{t.trend}</h4><p className="text-xs text-[#5C5C58] mt-2">{t.descrizione}</p><p className="text-xs mt-2 text-[#2A4B3C]">→ {t.come_sfruttarlo}</p></div>)}
        </div>
      </Panel>
      <Funnel list={s.funnel || []} />
      {s.piano_settimanale && <Week list={s.piano_settimanale} />}
      <Panel testid="replies-panel" title="Risposte modello ai commenti">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{(s.risposta_commenti || []).map((r, i) => <div key={i} className="border border-[#E8E8E4] p-4"><div className="label !text-[9px]">{r.situazione}</div><p className="text-sm mt-2">{r.risposta_modello}</p></div>)}</div>
      </Panel>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SimpleList testid="loyalty-panel" title="Fidelizzare i follower" items={s.fidelizzazione} />
        <SimpleList testid="mistakes-panel" title="Errori da evitare" items={s.errori_da_evitare} accent />
      </div>
    </div>
  );
}
